import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import { LanguageService } from '../../../core/i18n/language.service';
import { Icon } from '../icon/icon';
import { BaseControl } from './base-control';

export interface SelectOption {
  value: string | number;
  label: string;
}

/** Per-instance id counter for aria wiring. */
let uid = 0;

/** Menu geometry: cap (matches the old max-h-64), anchor gap, viewport margin. */
const MENU_MAX_HEIGHT = 256;
const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Dropdown matching the Papercast `Dropdown` design: a 48px raised trigger
 * (card fill, toolbar shadow, semibold action-ink label, trailing caret)
 * opening a `ListMenu` popup (overlay shadow, 48px rows, blue-wash hover,
 * action-fill selected row) — NOT a native <select>, which can't be styled to
 * match. Accepts `SelectOption[]` or plain string options.
 * ControlValueAccessor, so it drops into `ngModel` / reactive forms.
 *
 *   <app-select [(ngModel)]="groupId" label="Platform" [options]="groups" placeholder="Select…" />
 *
 * `size="sm"` renders the 32px trigger; `variant="text"` is the chromeless
 * "text dropdown" (no fill/shadow) for inline use.
 *
 * Keyboard: Enter/Space/ArrowDown/ArrowUp open; while open, ArrowUp/Down move the
 * active option, Home/End jump to first/last, Enter/Space commit the active
 * option, Escape closes. The listbox is wired with aria-haspopup/expanded/
 * activedescendant so it stays a real <select> replacement for AT users.
 *
 * Dismissal is hand-rolled (no @angular/cdk): click-outside via a document
 * mousedown listener bound while open, plus Escape.
 *
 * The open menu is position:fixed at coordinates measured from the field, NOT
 * absolute within it — inside a scroll container (e.g. the modal body) an
 * absolute menu inflates the container's scroll height and gets clipped. Fixed
 * coordinates go stale if an ancestor scrolls or the viewport resizes, so the
 * menu closes on either (matching native <select> behaviour) rather than
 * chasing the anchor. It flips above the field when the viewport below is tight.
 */
@Component({
  selector: 'app-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => Select), multi: true }],
  host: {
    // Close on outside click; no-op unless the menu is open.
    '(document:mousedown)': 'onDocumentMouseDown($event)',
  },
  template: `
    <div class="relative">
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        [attr.aria-expanded]="open()"
        [attr.aria-controls]="listboxId"
        [attr.aria-activedescendant]="open() && activeIndex() >= 0 ? optionId(activeIndex()) : null"
        [disabled]="disabled()"
        (click)="toggle()"
        (keydown)="onKeydown($event)"
        (blur)="onTouched()"
        class="trigger flex w-full items-center rounded-control pl-4 pr-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-60"
        [class.min-h-12]="size() === 'default'"
        [class.min-h-8]="size() === 'sm'"
        [class.bg-card]="variant() === 'default'"
        [class.shadow-toolbar]="variant() === 'default'"
        [class.ring-1]="invalid()"
        [class.ring-danger]="invalid()"
      >
        <span
          class="lbl min-w-0 flex-1 truncate text-[16px] leading-5"
          [class.font-semibold]="selectedLabel() !== null"
          [class.text-action]="selectedLabel() !== null"
          [class.text-ink-2]="selectedLabel() === null"
        >
          {{ selectedLabel() ?? (placeholder() || label()) }}
        </span>
        <app-icon
          class="caret flex-none text-ink-2"
          [name]="open() ? 'icon-arrow-collapse' : 'icon-arrow-dropdown'"
          [size]="24"
        />
      </button>

      @if (open() && !disabled()) {
        <div
          role="listbox"
          [id]="listboxId"
          class="fixed z-50 overflow-y-auto overflow-x-hidden rounded-control bg-card shadow-overlay"
          [style.left.px]="menuLeft()"
          [style.width.px]="menuWidth()"
          [style.top.px]="menuTop()"
          [style.bottom.px]="menuBottom()"
          [style.max-height.px]="menuMaxHeight()"
        >
          @for (opt of displayOptions(); track $index; let i = $index) {
            <div
              role="option"
              [id]="optionId(i)"
              [attr.aria-selected]="opt.value === value()"
              (mousedown)="select(opt.value)"
              (mouseenter)="activeIndex.set(i)"
              class="row flex h-12 cursor-pointer items-center pl-3 pr-1 text-[16px] leading-5"
              [class.selected]="opt.value === value()"
              [class.active]="i === activeIndex() && opt.value !== value()"
              [class.text-ink-1]="opt.value !== null && opt.value !== value()"
              [class.text-ink-2]="opt.value === null && opt.value !== value()"
            >
              <span class="min-w-0 flex-1 truncate">{{ opt.label }}</span>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: `
    /* Hover: the caret darkens to full ink (the label is already action ink). */
    .trigger:hover:not(:disabled) .lbl,
    .trigger:hover:not(:disabled) .caret {
      color: var(--ink-1);
    }

    /* ListMenu row states. Hover/keyboard-active is the 10% black downstate
       wash; the selected row is the action fill, label in on-button ink. */
    .row.active {
      background: var(--overlay-down-1);
    }
    .row.selected {
      background: var(--action-fill);
      color: var(--action-text);
    }
  `,
})
export class Select extends BaseControl<string | number> {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly transloco = inject(TranslocoService);
  private readonly language = inject(LanguageService);

  readonly options = input<(SelectOption | string)[]>([]);
  readonly label = input('');
  readonly placeholder = input('');
  readonly invalid = input(false);
  /** Trigger height: 48px default, 32px small (toolbars, pagination). */
  readonly size = input<'default' | 'sm'>('default');
  /** `text` is the chromeless "text dropdown" (no fill/shadow) for inline use. */
  readonly variant = input<'default' | 'text'>('default');
  /**
   * When true, the menu offers an explicit "clear" entry (labelled from
   * `placeholder`, defaulting to "None") that sets the value back to null — for
   * nullable fields that need to be un-set through the UI, not just left empty.
   */
  readonly clearable = input(false);

  protected readonly open = signal(false);
  /** Index of the keyboard/hover-highlighted option while the menu is open. */
  protected readonly activeIndex = signal(-1);

  /** Fixed-position menu geometry, measured from the field on open. Exactly one
   *  of top/bottom is set, depending on which side of the field the menu opens. */
  protected readonly menuLeft = signal(0);
  protected readonly menuWidth = signal(0);
  protected readonly menuTop = signal<number | null>(null);
  protected readonly menuBottom = signal<number | null>(null);
  protected readonly menuMaxHeight = signal(MENU_MAX_HEIGHT);

  /** Stable id roots for aria-controls / aria-activedescendant wiring. */
  protected readonly listboxId = `select-listbox-${uid++}`;
  protected optionId(i: number): string {
    return `${this.listboxId}-opt-${i}`;
  }

  protected readonly normalized = computed<SelectOption[]>(() =>
    this.options().map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
  );

  /**
   * The rows actually rendered in the menu: the real options, optionally
   * prefixed with a null-valued clear entry when `clearable` is set. Keyboard
   * navigation and selection index into this list.
   */
  protected readonly displayOptions = computed<{ value: string | number | null; label: string }[]>(
    () => {
      const opts = this.normalized();
      if (!this.clearable()) return opts;
      // Depend on the active language so the fallback re-evaluates on switch.
      this.language.current();
      return [
        { value: null, label: this.placeholder() || this.transloco.translate('forms.select.none') },
        ...opts,
      ];
    },
  );

  /** Label of the currently-selected option, or null when nothing is chosen. */
  protected readonly selectedLabel = computed<string | null>(() => {
    const v = this.value();
    if (v == null) return null;
    return this.normalized().find((o) => o.value === v)?.label ?? null;
  });

  constructor() {
    super();
    // When the menu opens, seed the active option to the current selection (or
    // the first option) so keyboard navigation starts from a sensible place.
    effect(() => {
      if (!this.open()) return;
      const opts = this.displayOptions();
      const selected = opts.findIndex((o) => o.value === this.value());
      this.activeIndex.set(selected >= 0 ? selected : opts.length ? 0 : -1);
    });

    // The fixed-position menu doesn't follow its anchor, so close it when an
    // ancestor scrolls or the viewport resizes. Capture-phase because scroll
    // events don't bubble out of inner scroll containers; scrolls inside the
    // menu itself are ignored.
    effect((onCleanup) => {
      if (!this.open()) return;
      const onScroll = (event: Event) => {
        if (this.host.nativeElement.contains(event.target as Node)) return;
        this.close();
      };
      const onResize = () => this.close();
      window.addEventListener('scroll', onScroll, { capture: true, passive: true });
      window.addEventListener('resize', onResize, { passive: true });
      onCleanup(() => {
        window.removeEventListener('scroll', onScroll, { capture: true });
        window.removeEventListener('resize', onResize);
      });
    });
  }

  protected toggle(): void {
    if (this.disabled()) return;
    if (this.open()) this.close();
    else this.openMenu();
  }

  /** Measure the field and place the menu below it, flipping above when the
   *  viewport below is too tight to be useful. */
  private openMenu(): void {
    const field = this.host.nativeElement.querySelector('button');
    if (!field) return;
    const rect = field.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN;

    this.menuLeft.set(rect.left);
    this.menuWidth.set(rect.width);
    if (spaceBelow >= MENU_MAX_HEIGHT || spaceBelow >= spaceAbove) {
      this.menuTop.set(rect.bottom + MENU_GAP);
      this.menuBottom.set(null);
      this.menuMaxHeight.set(Math.min(MENU_MAX_HEIGHT, spaceBelow));
    } else {
      this.menuTop.set(null);
      this.menuBottom.set(window.innerHeight - rect.top + MENU_GAP);
      this.menuMaxHeight.set(Math.min(MENU_MAX_HEIGHT, spaceAbove));
    }
    this.open.set(true);
  }

  protected close(): void {
    this.open.set(false);
  }

  protected select(value: string | number | null): void {
    this.setValue(value);
    this.onTouched();
    this.close();
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    const opts = this.displayOptions();

    if (!this.open()) {
      // Closed: Enter/Space/ArrowUp/ArrowDown open the menu.
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.openMenu();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1, opts.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1, opts.length);
        break;
      case 'Home':
        event.preventDefault();
        if (opts.length) this.activeIndex.set(0);
        break;
      case 'End':
        event.preventDefault();
        if (opts.length) this.activeIndex.set(opts.length - 1);
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const active = opts[this.activeIndex()];
        if (active) this.select(active.value);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        // Let focus leave; just dismiss the popup.
        this.close();
        break;
    }
  }

  private moveActive(delta: number, count: number): void {
    if (count === 0) return;
    const next = (this.activeIndex() + delta + count) % count;
    this.activeIndex.set(next);
  }

  protected onDocumentMouseDown(event: MouseEvent): void {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) this.close();
  }
}
